import { isEmpty } from 'lodash'
import { DateTime } from 'luxon'

export const ACCEPTED_DATE_LABEL = 'Accepted date'

export const ACCEPTED_DATE_DEFAULT_DISPLAY_LABEL =
  'Accepted Date (Default value)'

export const PURCHASE_TERMS_CUSTOMIZED_META_KEY = 'labraPurchaseTermsCustomized'

export type PurchaseTermsMetaDataItem = {
  key?: string
  value?: string | null
}

export const isPurchaseTermsCustomizedFromMeta = (
  metaData?: PurchaseTermsMetaDataItem[]
): boolean =>
  metaData?.some(
    item =>
      item.key === PURCHASE_TERMS_CUSTOMIZED_META_KEY && item.value === 'true'
  ) ?? false

export const applyPurchaseTermsMetaData = (
  metaData: PurchaseTermsMetaDataItem[] | undefined,
  purchaseTermsCustomized?: boolean
): PurchaseTermsMetaDataItem[] => {
  const filtered = (metaData || []).filter(
    item => item.key !== PURCHASE_TERMS_CUSTOMIZED_META_KEY
  )

  if (purchaseTermsCustomized) {
    filtered.push({
      key: PURCHASE_TERMS_CUSTOMIZED_META_KEY,
      value: 'true',
    })
  }

  return filtered
}

export const parseAcceptedDateMode = (variableStartDate: unknown): boolean => {
  if (variableStartDate === true || variableStartDate === 'true') return true
  if (variableStartDate === false || variableStartDate === 'false') return false
  return true
}

export const normalizeVariableStartDate = (
  variableStartDate: unknown
): boolean => parseAcceptedDateMode(variableStartDate)

export const inferPurchaseTermsCustomized = ({
  variableStartDate,
  offerStartDate,
  offerExpirationDate,
  createdAt,
  metaData,
}: {
  variableStartDate?: unknown
  offerStartDate?: string | null
  offerExpirationDate?: string | null
  createdAt?: string | null
  metaData?: PurchaseTermsMetaDataItem[]
}): boolean => {
  if (isPurchaseTermsCustomizedFromMeta(metaData)) return true
  if (!parseAcceptedDateMode(variableStartDate)) return true
  return !isDefaultAcceptedDatePurchaseTerms({
    variableStartDate,
    offerStartDate,
    offerExpirationDate,
    createdAt,
  })
}

export const resolvePurchaseTermsCustomized = ({
  purchaseTermsCustomized,
  variableStartDate,
  offerStartDate,
  offerExpirationDate,
  createdAt,
  metaData,
}: {
  purchaseTermsCustomized?: boolean
  variableStartDate?: unknown
  offerStartDate?: string | null
  offerExpirationDate?: string | null
  createdAt?: string | null
  metaData?: PurchaseTermsMetaDataItem[]
}): boolean =>
  Boolean(purchaseTermsCustomized) ||
  inferPurchaseTermsCustomized({
    variableStartDate,
    offerStartDate,
    offerExpirationDate,
    createdAt,
    metaData,
  })

export const shouldOpenPurchaseTermsEditView = ({
  purchaseTermsCustomized,
  variableStartDate,
  offerStartDate,
  offerExpirationDate,
  createdAt,
  metaData,
}: {
  purchaseTermsCustomized?: boolean
  variableStartDate?: unknown
  offerStartDate?: string | null
  offerExpirationDate?: string | null
  createdAt?: string | null
  metaData?: PurchaseTermsMetaDataItem[]
}): boolean =>
  resolvePurchaseTermsCustomized({
    purchaseTermsCustomized,
    variableStartDate,
    offerStartDate,
    offerExpirationDate,
    createdAt,
    metaData,
  })

export const getDefaultPurchaseExpiryDate = () =>
  DateTime.now().plus({ months: 1 }).endOf('month').toISO() as string

const AZURE_OFFER_DATE_DISPLAY_HOUR = 18
const AZURE_OFFER_DATE_DISPLAY_MINUTE = 30

const parseDateInUserTimeZone = (
  date: string,
  timeZone: string
): DateTime | null => {
  const inTimeZone = DateTime.fromISO(date, { zone: timeZone })
  if (inTimeZone.isValid) {
    return inTimeZone
  }

  const fromUtc = DateTime.fromISO(date, { zone: 'utc' }).setZone(timeZone)
  return fromUtc.isValid ? fromUtc : null
}

export const toPurchaseStartMonthPickerDate = (
  date: string | null | undefined,
  timeZone = 'UTC'
): string | null => {
  if (isEmpty(date)) {
    return null
  }

  const parsed = parseDateInUserTimeZone(String(date), timeZone)
  if (!parsed) {
    return null
  }

  return parsed.startOf('month').toFormat('yyyy-MM-dd')
}

export const toPurchaseStartMonthForSubmit = (
  date: string | null | undefined,
  timeZone = 'UTC'
): string | null => {
  if (isEmpty(date)) {
    return null
  }

  const parsed = parseDateInUserTimeZone(String(date), timeZone)
  if (!parsed) {
    return null
  }

  return parsed
    .startOf('month')
    .endOf('month')
    .set({
      hour: AZURE_OFFER_DATE_DISPLAY_HOUR,
      minute: AZURE_OFFER_DATE_DISPLAY_MINUTE,
      second: 0,
      millisecond: 0,
    })
    .toUTC()
    .toISO()
}

export const toAcceptByDatePickerDate = (
  date: string | null | undefined,
  timeZone = 'UTC'
): string | null => {
  if (isEmpty(date)) {
    return null
  }

  const parsed = parseDateInUserTimeZone(String(date), timeZone)
  if (!parsed) {
    return null
  }

  return parsed.toFormat('yyyy-MM-dd')
}

export const toAcceptByDateForSubmit = (
  date: string | null | undefined,
  timeZone = 'UTC'
): string | null => {
  if (isEmpty(date)) {
    return null
  }

  const parsed = parseDateInUserTimeZone(String(date), timeZone)
  if (!parsed) {
    return null
  }

  return parsed
    .startOf('day')
    .set({
      hour: AZURE_OFFER_DATE_DISPLAY_HOUR,
      minute: AZURE_OFFER_DATE_DISPLAY_MINUTE,
      second: 0,
      millisecond: 0,
    })
    .toUTC()
    .toISO()
}

export const isDefaultAcceptedDatePurchaseTerms = ({
  variableStartDate,
  offerStartDate,
  offerExpirationDate,
  createdAt,
}: {
  variableStartDate?: unknown
  offerStartDate?: string | null
  offerExpirationDate?: string | null
  createdAt?: string | null
}): boolean => {
  if (!parseAcceptedDateMode(variableStartDate)) return false
  if (!isEmpty(offerStartDate)) return false
  if (!offerExpirationDate) return true

  const savedExp = DateTime.fromISO(String(offerExpirationDate), {
    zone: 'utc',
  })

  const expiryMatchesMonth = (reference: DateTime) =>
    savedExp.year === reference.year && savedExp.month === reference.month

  if (createdAt) {
    const createdAtDefaultExpiry = DateTime.fromISO(createdAt, { zone: 'utc' })
      .plus({ months: 1 })
      .endOf('month')
    if (expiryMatchesMonth(createdAtDefaultExpiry)) {
      return true
    }
  }

  return expiryMatchesMonth(DateTime.now().plus({ months: 1 }).endOf('month'))
}

export const getPurchaseStartDateDisplayValue = ({
  variableStartDate,
  offerStartDate,
  offerExpirationDate,
  createdAt,
  purchaseTermsCustomized,
}: {
  variableStartDate?: unknown
  offerStartDate?: string | null
  offerExpirationDate?: string | null
  createdAt?: string | null
  purchaseTermsCustomized?: boolean
}): string => {
  if (!parseAcceptedDateMode(variableStartDate)) {
    return ''
  }

  if (purchaseTermsCustomized) {
    return ACCEPTED_DATE_LABEL
  }

  if (
    isDefaultAcceptedDatePurchaseTerms({
      variableStartDate,
      offerStartDate,
      offerExpirationDate,
      createdAt,
    })
  ) {
    return ACCEPTED_DATE_DEFAULT_DISPLAY_LABEL
  }

  return ACCEPTED_DATE_LABEL
}

export const normalizeAzurePurchaseTermsForSubmit = ({
  variableStartDate,
  offerStartDate,
  offerExpirationDate,
  purchaseTermsCustomized,
}: {
  variableStartDate?: unknown
  offerStartDate?: string | null
  offerExpirationDate?: string | null
  purchaseTermsCustomized?: boolean
}) => {
  const isAcceptedDate = parseAcceptedDateMode(variableStartDate)

  if (isAcceptedDate) {
    return {
      variableStartDate: true,
      offerStartDate: null as string | null,
      offerExpirationDate: isEmpty(offerExpirationDate)
        ? purchaseTermsCustomized
          ? null
          : getDefaultPurchaseExpiryDate()
        : String(offerExpirationDate),
    }
  }

  return {
    variableStartDate: false,
    offerStartDate: isEmpty(offerStartDate) ? null : String(offerStartDate),
    offerExpirationDate: isEmpty(offerExpirationDate)
      ? null
      : String(offerExpirationDate),
  }
}

export type ClonePurchaseTerms = {
  offerExpirationDate?: string | null
  offerStartDate?: string | null
  variableStartDate?: boolean
  purchaseTermsCustomized?: boolean
}

export type ClonePurchaseTermOfferData = {
  offerExpirationDate?: string | null
  offerStartDate?: string | null
  variableStartDate?: unknown
  purchaseTermsCustomized?: boolean
  metaData?: PurchaseTermsMetaDataItem[]
}

export const getClonePurchaseTermsFromOffer = ({
  variableStartDate,
  offerStartDate,
  offerExpirationDate,
  createdAt,
  metaData,
}: {
  variableStartDate?: unknown
  offerStartDate?: string | null
  offerExpirationDate?: string | null
  createdAt?: string | null
  metaData?: PurchaseTermsMetaDataItem[]
}): ClonePurchaseTerms => {
  const purchaseTermsCustomized = resolvePurchaseTermsCustomized({
    variableStartDate,
    offerStartDate,
    offerExpirationDate,
    createdAt,
    metaData,
  })

  if (!purchaseTermsCustomized) {
    return {
      offerExpirationDate: null,
      offerStartDate: null,
      variableStartDate: true,
      purchaseTermsCustomized: false,
    }
  }

  return {
    offerExpirationDate: null,
    offerStartDate: null,
    variableStartDate: normalizeVariableStartDate(variableStartDate),
    purchaseTermsCustomized: true,
  }
}

const normalizeClonePurchaseTermDate = ({
  cloneTerms,
  field,
  fallback,
  boundary,
}: {
  cloneTerms: ClonePurchaseTerms
  field: 'offerExpirationDate' | 'offerStartDate'
  fallback: unknown
  boundary: 'start' | 'end'
}) => {
  if (!(field in cloneTerms)) {
    return fallback
  }

  const value = cloneTerms[field]
  if (!value) {
    return null
  }

  const dateTime = DateTime.fromISO(String(value), { zone: 'utc' })
  return (
    boundary === 'start' ? dateTime.startOf('month') : dateTime.endOf('month')
  ).toISO() as string
}

export const mergeClonePurchaseTermsWithOfferData = <
  T extends ClonePurchaseTermOfferData,
>(
  privateOfferData: T,
  cloneTerms: ClonePurchaseTerms
): T => {
  const merged = {
    ...privateOfferData,
    offerExpirationDate: normalizeClonePurchaseTermDate({
      cloneTerms,
      field: 'offerExpirationDate',
      fallback: privateOfferData.offerExpirationDate,
      boundary: 'end',
    }),
    offerStartDate: normalizeClonePurchaseTermDate({
      cloneTerms,
      field: 'offerStartDate',
      fallback: privateOfferData.offerStartDate,
      boundary: 'start',
    }),
    variableStartDate:
      cloneTerms.variableStartDate !== undefined
        ? cloneTerms.variableStartDate
        : normalizeVariableStartDate(privateOfferData.variableStartDate),
    purchaseTermsCustomized:
      cloneTerms.purchaseTermsCustomized !== undefined
        ? cloneTerms.purchaseTermsCustomized
        : privateOfferData.purchaseTermsCustomized,
  }

  return {
    ...merged,
    metaData: applyPurchaseTermsMetaData(
      merged.metaData,
      Boolean(merged.purchaseTermsCustomized)
    ),
  }
}
